import api from '@/shared/utils/apiConfig';
import i18n from '@/shared/i18n';
import type { ShiftTypeBreakMode } from '../shift-types/shiftTypeForm';

export type { ShiftTypeBreakMode };
export type ShiftTypeStatus = 'active' | 'inactive';

export interface ShiftTypeClinicRef {
  id: string;
  name?: string;
  shortName?: string;
}

export interface ShiftTypeRecord {
  id: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  breakMode: ShiftTypeBreakMode;
  breakStartTime?: string;
  breakEndTime?: string;
  breakMinutes: number;
  graceMinutesLate: number;
  graceMinutesEarly: number;
  lateDeductMultiplier: number;
  color?: string;
  status: ShiftTypeStatus;
  applicableClinicIds: string[];
  applicableClinics?: ShiftTypeClinicRef[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: { id: string; name: string } | null;
  updatedBy?: { id: string; name: string } | null;
}

export interface ListShiftTypesParams {
  status?: ShiftTypeStatus;
  clinicId?: string;
  sort?: string;
}

export interface CreateShiftTypePayload {
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  breakMode?: ShiftTypeBreakMode;
  breakStartTime?: string;
  breakEndTime?: string;
  breakMinutes?: number;
  graceMinutesLate?: number;
  graceMinutesEarly?: number;
  lateDeductMultiplier?: number;
  color?: string;
  applicableClinicIds?: string[];
  status?: ShiftTypeStatus;
}

export interface UpdateShiftTypePayload {
  code?: string;
  name?: string;
  startTime?: string;
  endTime?: string;
  breakMode?: ShiftTypeBreakMode;
  breakStartTime?: string;
  breakEndTime?: string;
  breakMinutes?: number;
  graceMinutesLate?: number;
  graceMinutesEarly?: number;
  lateDeductMultiplier?: number;
  color?: string;
  applicableClinicIds?: string[];
  status?: ShiftTypeStatus;
}

export const listShiftTypes = async (
  status?: ShiftTypeStatus,
  params: Omit<ListShiftTypesParams, 'status'> = {}
): Promise<ShiftTypeRecord[]> => {
  const response = await api.get('/scheduling/shift-types', {
    params: { status, ...params },
  });
  return response.data.data;
};

export const createShiftType = async (
  payload: CreateShiftTypePayload
): Promise<ShiftTypeRecord> => {
  const response = await api.post('/scheduling/shift-types', payload);
  return response.data.data;
};

export const updateShiftType = async (
  id: string,
  payload: UpdateShiftTypePayload
): Promise<ShiftTypeRecord> => {
  const response = await api.put(`/scheduling/shift-types/${id}`, payload);
  return response.data.data;
};

export const updateShiftTypeStatus = async (
  id: string,
  status: ShiftTypeStatus
): Promise<ShiftTypeRecord> => {
  const response = await api.patch(`/scheduling/shift-types/${id}/status`, { status });
  return response.data.data;
};

export const SHIFT_TYPE_STATUS_LABELS: Record<ShiftTypeStatus, string> = {
  get active() { return i18n.t('scheduling:entityStatus.active'); },
  get inactive() { return i18n.t('scheduling:entityStatus.inactive'); },
};
